// The Whole Story – Swiper initialiser for every twst slider.
// Markup: <div data-twst-swiper-scope> … <div class="swiper" data-twst-swiper='{"perView":{"0":2,"750":3,"1200":4},"gap":{"0":0}}'>
//   <div class="swiper-wrapper"><div class="swiper-slide">…</div></div></div> … [data-prev] [data-next] </div>
// Options: perView / gap per min-width breakpoint, marquee (continuous autoplay), speed (ms per slide).
(() => {
  if (window.twstSwiper) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const breakpointsFrom = (o) => {
    const keys = new Set([...Object.keys(o.perView || { 0: 1 }), ...Object.keys(o.gap || {})]);
    const sorted = [...keys].map(Number).sort((a, b) => a - b);
    const pick = (map, bp, fallback) => {
      let value = fallback;
      Object.keys(map || {}).map(Number).sort((a, b) => a - b).forEach((k) => {
        if (k <= bp) value = Number(map[k]);
      });
      return value;
    };
    const bps = {};
    sorted.forEach((bp) => {
      bps[bp] = { slidesPerView: pick(o.perView, bp, 1), spaceBetween: pick(o.gap, bp, 0) };
    });
    return bps;
  };

  // Two-finger (trackpad) horizontal scrolling: the slider follows the fingers, then snaps to the nearest slide.
  // Vertical swipes are left alone so the page still scrolls.
  const trackpad = (swiper) => {
    let snap;
    swiper.el.addEventListener(
      'wheel',
      (e) => {
        if (swiper.isLocked || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();
        const next = Math.min(swiper.minTranslate(), Math.max(swiper.maxTranslate(), swiper.translate - e.deltaX));
        swiper.setTransition(0);
        swiper.setTranslate(next);
        swiper.updateProgress();
        swiper.updateActiveIndex();
        swiper.updateSlidesClasses();
        clearTimeout(snap);
        snap = setTimeout(() => swiper.slideToClosest(300), 120);
      },
      { passive: false }
    );
  };

  // Looping marquee: a horizontal two-finger swipe steps one card back / forward
  const trackpadStep = (swiper) => {
    let sum = 0;
    let idle;
    swiper.el.addEventListener(
      'wheel',
      (e) => {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();
        sum += e.deltaX;
        clearTimeout(idle);
        idle = setTimeout(() => (sum = 0), 200);
        if (Math.abs(sum) < 40) return;
        if (sum > 0) swiper.slideNext(500);
        else swiper.slidePrev(500);
        sum = 0;
      },
      { passive: false }
    );
  };

  const init = (el) => {
    if (el.swiper || typeof Swiper === 'undefined') return;
    const o = JSON.parse(el.dataset.twstSwiper || '{}');
    const scope = el.closest('[data-twst-swiper-scope]') || el.parentElement;
    const bps = breakpointsFrom(o);
    const marquee = Boolean(o.marquee) && !reduced;

    const swiper = new Swiper(el, {
      ...bps[0],
      breakpoints: bps,
      watchOverflow: true,
      grabCursor: true,
      // no rewind: the arrows stop (and show as disabled) at the first / last slide
      rewind: false,
      loop: marquee,
      speed: marquee ? Number(o.speed || 6000) : 500,
      on: {
        lock: () => scope.setAttribute('data-static', ''),
        unlock: () => scope.removeAttribute('data-static'),
      },
    });
    if (swiper.isLocked) scope.setAttribute('data-static', '');

    if (marquee) trackpadStep(swiper);
    else trackpad(swiper);

    const prevs = [...scope.querySelectorAll('[data-prev]')];
    const nexts = [...scope.querySelectorAll('[data-next]')];
    prevs.forEach((b) => b.addEventListener('click', () => swiper.slidePrev()));
    nexts.forEach((b) => b.addEventListener('click', () => swiper.slideNext()));

    if (!marquee && (prevs.length || nexts.length)) {
      // Arrow state: faded at the first / last slide
      const sync = () => {
        prevs.forEach((b) => (b.disabled = swiper.isBeginning));
        nexts.forEach((b) => (b.disabled = swiper.isEnd));
      };
      swiper.on('slideChange resize breakpoint reachBeginning reachEnd fromEdge', sync);
      sync();
    }

    if (marquee) {
      el.classList.add('twst-swiper--marquee');
      let paused = false;
      let visible = false;
      const run = () => {
        if (!paused && visible && !swiper.destroyed) swiper.slideNext(Number(o.speed || 6000));
      };
      swiper.on('transitionEnd', run);
      el.addEventListener('mouseenter', () => { paused = true; });
      el.addEventListener('mouseleave', () => { paused = false; run(); });
      swiper.on('touchEnd', () => setTimeout(run, 50));
      new IntersectionObserver((entries) => {
        visible = entries[0].isIntersecting;
        run();
      }).observe(el);
    }
  };

  // "maxWidth": the slider only exists below this viewport width (e.g. a card deck that is a slider on mobile only)
  const setup = (el) => {
    const max = Number(JSON.parse(el.dataset.twstSwiper || '{}').maxWidth || 0);
    if (!max) return init(el);
    const mq = window.matchMedia(`(max-width: ${max}px)`);
    const apply = () => {
      if (mq.matches) init(el);
      else if (el.swiper) el.swiper.destroy(true, true);
    };
    apply();
    mq.addEventListener('change', apply);
  };

  // Initialise each slider only when it comes near the viewport (keeps Swiper's work out of the initial page load).
  // "eager": true in the options (e.g. the product gallery at the top of the page) or the theme editor → right away.
  const lazySetup = (el) => {
    const eager = Boolean(JSON.parse(el.dataset.twstSwiper || '{}').eager);
    if (eager || window.Shopify?.designMode || !('IntersectionObserver' in window)) return setup(el);
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        setup(el);
      },
      { rootMargin: '400px 0px' }
    );
    io.observe(el);
  };

  const scan = (root = document) => root.querySelectorAll('[data-twst-swiper]').forEach(lazySetup);

  window.twstSwiper = { init, scan };
  scan();
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-twst-swiper]').forEach((el) => el.swiper?.destroy(true, true));
  });
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest('.swiper-slide');
    const el = slide?.closest('[data-twst-swiper]');
    if (el?.swiper) el.swiper.slideTo([...slide.parentElement.children].indexOf(slide));
  });
})();
